import { ProductivityTierKey, ScreeningInsightsResponse } from '@estercare/ester-shared'
import { theme } from '../../theme/theme'
import { ChartStep, SegmentRow } from '../../components/charts/type'
import { SEVERITY_KEYS } from './severityByCondition'

export const IMPACT_BAND_KEYS = {
  low: 'low',
  moderate: 'moderate',
  high: 'high',
} as const

export type ImpactBandKey = (typeof IMPACT_BAND_KEYS)[keyof typeof IMPACT_BAND_KEYS]

const IMPACT_BAND_KEY_SET: ReadonlySet<string> = new Set(Object.values(IMPACT_BAND_KEYS))

const isKnownImpactBandKey = (key: string): key is ImpactBandKey => IMPACT_BAND_KEY_SET.has(key)

// One hue stepping up: impact reads as "more of the same", not as a traffic light.
export const getImpactSteps = (t: (key: string) => string): ChartStep[] => [
  {
    key: IMPACT_BAND_KEYS.low,
    name: t('admin.statistics.impactLow'),
    color: theme.color.beigeDark,
    ink: theme.color.plum,
  },
  {
    key: IMPACT_BAND_KEYS.moderate,
    name: t('admin.statistics.impactModerate'),
    color: theme.color.mauve,
    ink: theme.color.plumDeep,
  },
  {
    key: IMPACT_BAND_KEYS.high,
    name: t('admin.statistics.impactHigh'),
    color: theme.color.plum,
    ink: theme.color.white,
  },
]

// Five keys, not three: the API pools tiers too thin to show on their own instead of dropping them.
const SEVERITY_TIER_LABEL_KEYS: Record<ProductivityTierKey, string> = {
  [SEVERITY_KEYS.mild]: 'admin.statistics.workImpactTierMild',
  [SEVERITY_KEYS.moderate]: 'admin.statistics.workImpactTierModerate',
  [SEVERITY_KEYS.severe]: 'admin.statistics.workImpactTierSevere',
  mildModerate: 'admin.statistics.workImpactTierMildModerate',
  all: 'admin.statistics.workImpactTierAll',
}

/** One row per tier the API returned, in its order. No tiers means the chart was suppressed. */
export const getProductivityRows = (
  productivityBySeverity: ScreeningInsightsResponse['productivityBySeverity'],
  t: (key: string) => string
): SegmentRow[] => {
  if (!productivityBySeverity) return []

  return productivityBySeverity.tiers.flatMap((tier) => {
    const labelKey = SEVERITY_TIER_LABEL_KEYS[tier.key]
    if (!labelKey) {
      // Closed union, so this only happens when our shared types are behind the API.
      // eslint-disable-next-line no-console
      console.warn(`Screening insights returned an unrecognised productivity tier key: ${tier.key}`)
      return []
    }

    return [
      {
        key: tier.key,
        label: t(labelKey),
        values: tier.bands.reduce<Record<string, number>>((acc, band) => {
          if (!isKnownImpactBandKey(band.key)) {
            // No step means no colour and no legend entry, so the band can't be drawn at all.
            // eslint-disable-next-line no-console
            console.warn(`Screening insights returned an unrecognised impact band key: ${band.key}`)
            return acc
          }
          return { ...acc, [band.key]: band.pct }
        }, {}),
        // Every band the API sent, drawable or not, so a dropped band leaves a gap.
        scaleTotal: tier.bands.reduce((sum, band) => sum + band.pct, 0),
      },
    ]
  })
}
